import { useNavigate, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function Security() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-[#000000] transition-colors duration-500">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&display=swap');

        * {
          font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(2deg); }
        }

        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .animate-float-delayed {
          animation: float 6s ease-in-out 1s infinite;
        }

        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient-shift 8s ease infinite;
        }

        .glass-card {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .dark .glass-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        .mesh-gradient {
          background:
            radial-gradient(at 27% 37%, hsla(215, 98%, 61%, 0.3) 0px, transparent 50%),
            radial-gradient(at 97% 21%, hsla(125, 98%, 72%, 0.2) 0px, transparent 50%),
            radial-gradient(at 52% 99%, hsla(354, 98%, 61%, 0.2) 0px, transparent 50%),
            radial-gradient(at 10% 29%, hsla(256, 96%, 67%, 0.2) 0px, transparent 50%),
            radial-gradient(at 97% 96%, hsla(38, 60%, 74%, 0.2) 0px, transparent 50%),
            radial-gradient(at 33% 50%, hsla(222, 67%, 73%, 0.2) 0px, transparent 50%),
            radial-gradient(at 79% 53%, hsla(343, 68%, 79%, 0.2) 0px, transparent 50%);
        }

        .grain {
          position: relative;
        }

        .grain::before {
          content: '';
          position: absolute;
          inset: 0;
          opacity: 0.03;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
          pointer-events: none;
        }
      `}</style>

      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-700 ${
        scrollY > 20
          ? 'bg-white/70 dark:bg-black/70 backdrop-blur-2xl border-b border-black/5 dark:border-white/5'
          : 'bg-transparent'
      }`}>
        <div className="max-w-[1400px] mx-auto px-8 py-5">
          <div className="flex items-center justify-between relative">
            {/* Logo */}
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2.5 group z-10"
            >
              <div className="relative w-8 h-8">
                <svg className="w-8 h-8 text-[#06c] dark:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-[21px] font-semibold text-gray-900 dark:text-white tracking-tight">
                QuickMeet
              </span>
            </button>

            {/* Center Navigation - Absolutely positioned */}
            <div className="hidden md:flex items-center gap-8 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <button
                onClick={() => navigate('/about')}
                className="text-[14px] font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                About
              </button>
              <button
                onClick={() => navigate('/pricing')}
                className="text-[14px] font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Pricing
              </button>
              <button
                onClick={() => navigate('/security')}
                className="text-[14px] font-medium text-gray-900 dark:text-white transition-colors"
              >
                Security
              </button>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-5 z-10">
              <button
                onClick={() => navigate('/')}
                className="hidden sm:block text-[14px] font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Home
              </button>

              <button
                onClick={() => navigate('/login')}
                className="px-5 py-2 bg-[#06c] dark:bg-white text-white dark:text-black text-[14px] font-medium rounded-full hover:bg-[#0055d4] dark:hover:bg-gray-100 transition-all duration-200"
              >
                Get started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden grain">
        <div className="absolute inset-0 mesh-gradient animate-gradient opacity-40 dark:opacity-20"></div>

        <div className="absolute top-20 left-20 w-72 h-72 bg-green-400 rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-3xl opacity-20 animate-float"></div>
        <div className="absolute top-40 right-20 w-72 h-72 bg-[#06c] rounded-full mix-blend-multiply dark:mix-blend-screen filter blur-3xl opacity-20 animate-float-delayed"></div>

        <div className="relative max-w-[1080px] mx-auto px-8 py-32 text-center">
          <div className="mb-12">
            <div className="w-32 h-32 bg-gradient-to-br from-green-500 to-[#06c] rounded-full mx-auto mb-8 flex items-center justify-center shadow-2xl shadow-green-500/30">
              <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
          </div>

          <h1 className="text-6xl md:text-8xl font-black text-gray-900 dark:text-white mb-8 leading-none tracking-tight">
            Your calls stay
            <br />
            <span className="bg-gradient-to-r from-green-500 via-[#06c] to-purple-600 bg-clip-text text-transparent animate-gradient">
              yours
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-16 max-w-3xl mx-auto font-medium leading-relaxed">
            We use peer-to-peer connections, which means your video goes straight from you to the other person. We never see it.
          </p>

          <div className="max-w-2xl mx-auto text-left space-y-6 text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
            <div className="glass-card rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-[#06c] rounded-xl flex items-center justify-center text-white flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                Direct Connection
              </h3>
              <p>
                When you join a call, your browser connects directly to the other person's browser. No middleman, no server storing your video. It's like a phone call, but with video.
              </p>
            </div>

            <div className="glass-card rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-[#06c] rounded-xl flex items-center justify-center text-white flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                Automatic Encryption
              </h3>
              <p>
                All video, audio, and messages are automatically encrypted by your browser. Even if someone intercepts the data, they can't read it. This is built into WebRTC—we didn't have to add it.
              </p>
            </div>

            <div className="glass-card rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-[#06c] rounded-xl flex items-center justify-center text-white flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                You Control Your Data
              </h3>
              <p>
                Live calls are never stored on our servers. If you choose to record a meeting or save notes, that content is stored in your account—but it's always your choice. We don't passively collect or track anything.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Simple Breakdown */}
      <section className="relative py-32 px-8 bg-gray-50 dark:bg-[#0a0a0a] grain">
        <div className="max-w-[1080px] mx-auto">
          <h2 className="text-5xl md:text-7xl font-black text-gray-900 dark:text-white mb-16 text-center">
            What we collect
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="glass-card rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-3xl">✅</span>
                What we store
              </h3>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li className="flex items-start gap-3">
                  <span className="text-green-500 flex-shrink-0">✓</span>
                  <span>Your email address (for login)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 flex-shrink-0">✓</span>
                  <span>Meeting IDs and timestamps</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 flex-shrink-0">✓</span>
                  <span>Recordings (only if you choose to record)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-500 flex-shrink-0">✓</span>
                  <span>Meeting notes (only if you save them)</span>
                </li>
              </ul>
            </div>

            <div className="glass-card rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <span className="text-3xl">❌</span>
                What we don't track
              </h3>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                <li className="flex items-start gap-3">
                  <span className="text-red-500 flex-shrink-0">✗</span>
                  <span>Live video or audio from calls</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 flex-shrink-0">✗</span>
                  <span>Chat messages (P2P only)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 flex-shrink-0">✗</span>
                  <span>Behavioral tracking or analytics</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-500 flex-shrink-0">✗</span>
                  <span>Third-party tracking (no cookies)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 px-8 bg-gradient-to-br from-green-500 to-[#06c] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white to-transparent animate-gradient"></div>
        </div>

        <div className="relative max-w-[1080px] mx-auto text-center">
          <h2 className="text-5xl md:text-7xl font-black text-white mb-8">
            Private by design
          </h2>
          <p className="text-xl md:text-2xl text-white/90 mb-12 font-medium">
            Start a secure call in seconds
          </p>

          <button
            onClick={() => navigate('/login')}
            className="group relative px-12 py-5 bg-white text-[#06c] rounded-2xl text-xl font-bold hover:shadow-2xl hover:scale-105 transition-all duration-300"
          >
            Start Secure Meeting
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 dark:bg-black text-gray-400 py-16 px-8">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#06c] to-[#0055d4] rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">QuickMeet</h3>
                  <span className="text-sm">by TheJhyeFactor</span>
                </div>
              </div>
              <p className="text-gray-400 max-w-md">
                Professional video conferencing made simple. Connect with anyone, anywhere, for free.
              </p>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/')} className="hover:text-white transition-colors">Home</button></li>
                <li><button onClick={() => navigate('/pricing')} className="hover:text-white transition-colors">Pricing</button></li>
                <li><button onClick={() => navigate('/security')} className="hover:text-white transition-colors">Security</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><button onClick={() => navigate('/about')} className="hover:text-white transition-colors">About</button></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm">© 2025 TheJhyeFactor. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="https://twitter.com/TheJhyeFactor" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                </svg>
              </a>
              <a href="https://www.linkedin.com/in/jhye-o-meley-529960213/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
